<template lang="pug">
div(:class="[{ check: check || editIsVisible, editIsVisible: editIsVisible, error, ['hideError']: error && noErrorMessage }, 'label-position-' + labelPosition, $style.txt]")

  .label(
    v-if="!['animated', 'border'].includes(labelPosition) && label"
    :class="[classes]")
    label.text(:for="txtId") {{ label }}

  .text-input
    .relative
      input.placeholder-gray-550(
        :id="txtId"
        :type="type"
        :value="value"
        :placeholder="placeholderText"
        :class="{[classes.join(' ')]: true, ['active']: activeFlag, ['showArrows']: showArrows}"
        @input="$emit('input', $event.target.value)"
        @blur="onBlur"
        @focus="onFocus"
        @change="$emit('change', $event.target.value)"
        :required="labelPosition == 'animated'"
        ref="input"
        step="any"
        :readonly="readonly"
        :maxlength="maxLength"
        :disabled="disabled || editIsVisible"
        :autocomplete="autocomplete"
        :style="styles.join(';')"
        :min="min"
        :max="max")

      div(
        v-if="iconLeft"
        class="left-0 ml-ds-xxxs"
        :class="$style.placeholderIcon")
        Icon(
          :color="iconColor"
          :name="iconLeft"
          :size="iconFontSize"
          :style="iconStyles.join(';')")

      div(
        v-if="iconRight && !editIsVisible && !check"
        class="right-0 mr-ds-xxxs"
        :class="$style.placeholderIcon")
        Icon(
          :color="iconColor"
          :name="iconRight"
          :size="iconFontSize"
          :style="iconStyles.join(';')")

      .check(
        v-show="check || editIsVisible"
        :class="[{ 'checkWithEdit': editIsVisible }, $style.placeholderIcon]")
        Icon(
          name="i:check-solid"
          :size="14")

      .edit(
        v-if="editIsVisible"
        :class="$style.placeholderIcon")
        a(@click="editIsClicked = true") Edit

      .label(
        v-if="['animated', 'border'].includes(labelPosition) && label"
        :class="[classes]")
        label.text(:for="txtId") {{ label }}

    .msg.flex(v-if="error && !noErrorMessage")
      Icon(
        name="i:triangle-exclamation-solid"
        :size="16")
      .text {{ error }}

</template>

<script>
import { getSizeClasses } from "~/scripts/css-helper"
import useUniqueId from "~/use/unique-id"

export default {
  name: "BaseTxt",

  props: {
    value: {
      type: [String, Number],
      default: ""
    },
    /** Add a green check icon */
    check: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ""
    },
    labelPosition: {
      type: String,
      default: "top",
      validator: value => ["top", "left", "animated", "border"].includes(value)
    },
    error: {
      type: String,
      default: ""
    },
    /** Hide error message - just red placeholder and border */
    noErrorMessage: {
      type: Boolean,
      default: false
    },
    /** Standard input types */
    type: {
      type: String,
      default: "text"
    },
    size: {
      type: String,
      default: "m",
      validator: value => ["xs", "s", "m", "l"].includes(value)
    },
    inputId: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: ""
    },
    maxLength: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    checkAndDisable: {
      type: Boolean,
      default: false
    },
    focused: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: String,
      default: "off",
      required: false
    },
    /** Icon shortcut */
    iconLeft: {
      type: String,
      default: ""
    },
    /** Icon shortcut */
    iconRight: {
      type: String,
      default: ""
    },
    /** Icon size */
    // eslint-disable-next-line vue/require-default-prop
    iconSize: {
      type: Number,
      required: false
    },
    /** Icon colour */
    // eslint-disable-next-line vue/require-default-prop
    iconColor: {
      type: String,
      required: false
    },
    min: {
      type: Number,
      required: false,
      default: null
    },
    max: {
      type: Number,
      required: false,
      default: null
    },
    showArrows: {
      type: Boolean,
      default: false
    },
    border: {
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String,
      default: ""
    },
    iconPadding: {
      type: Number,
      required: false,
      default: null
    }
  },

  data: () => ({
    activeFlag: false,
    txtId: "",
    editIsClicked: false,
    xPadding: null
  }),

  computed: {
    classes() {
      return getSizeClasses(this.size)
    },

    placeholderText() {
      return this.labelPosition !== "animated" ? this.placeholder : ""
    },

    editIsVisible() {
      return this.checkAndDisable && !this.editIsClicked
    },

    iconFontSize() {
      let size = 16

      if (this.iconSize) {
        size = this.iconSize
      } else {
        switch (this.size) {
          case "xs":
            size = 16
            break
          case "s":
            size = 18
            break
          case "m":
            size = 20
            break
          case "l":
            size = 24
            break
        }
      }

      return size
    },

    iconStyles() {
      const styles = []

      if (this.iconLeft || this.iconRight) {
        styles.push(`width: ${this.xPadding}px`)
      }

      if (this.iconColor) {
        styles.push(`color: ${this.iconColor}`)
      }

      return styles
    },

    styles() {
      const styles = []

      if (this.iconLeft) {
        styles.push(`padding-left: ${this.xPadding}px`)
      }

      if (this.iconRight) {
        styles.push(`padding-right: ${this.xPadding}px`)
      }

      if (!this.border) {
        styles.push(`border: 0`)
      }

      if (this.backgroundColor) {
        styles.push(`background-color: ${this.backgroundColor}`)
      }

      return styles
    }
  },

  mounted() {
    if (this.focused) {
      this.focus()
    }

    if (this.iconLeft || this.iconRight) {
      if (this.iconPadding) {
        this.xPadding = this.iconPadding
      } else {
        this.xPadding = this.iconSize ? this.iconSize * 2 : this.iconFontSize * 2
      }
    }

    this.txtId = this.inputId ? this.inputId : "txt-" + useUniqueId()

    this.$emit("input-loaded")
  },

  methods: {
    focus() {
      this.$refs.input.focus()
    },

    onFocus() {
      this.activeFlag = true

      if (this.autocomplete === "off" && this.$refs.input) {
        this.$refs.input.setAttribute("autocomplete", "chrome-off")
      }

      this.$emit("focus")
    },

    onBlur() {
      this.activeFlag = false

      if (this.autocomplete === "off" && this.$refs.input) {
        this.$refs.input.setAttribute("autocomplete", "off")
      }

      this.$emit("blur")
    }
  }
}
</script>

<style lang="scss" module>
$inputPadding: 15px;

:global {
  :local(.txt) {
    position: relative;

    input {
      background-color: white;
      border: 1px solid #c7c7c7;
      padding: $inputPadding;
      border-radius: var(--border-radius);
      color: #6e6e6e;
      opacity: 1; /* required on iOS */
      width: 100%;

      &:focus:not([readonly]) {
        @apply border-blue-primary;
      }

      &.xs-size {
        padding: 8px;
      }

      &[readonly],
      &[disabled] {
        cursor: not-allowed;
      }

      &:not(.showArrows) {
        &[type="number"]::-webkit-inner-spin-button,
        &[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          margin: 0;
        }

        &[type="number"] {
          -moz-appearance: textfield;
        }
      }
    }

    &.error {
      input {
        @apply border-red-primary;
        border-bottom-color: transparent;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    &.hideError {
      input {
        @apply border-red-primary rounded text-red-primary;

        &::placeholder {
          @apply text-red-primary;
        }
      }
    }

    &.label-position-left {
      display: flex;
    }

    input:focus ~ .msg {
      box-shadow: 0 0 0 2px lighten(#c7c7c7, 10%);
    }

    .text-input {
      position: relative;
    }

    .label {
      display: table;
      padding-right: 1em;

      .text {
        vertical-align: middle;
        display: table-cell;
      }
    }

    .msg {
      @apply font-latoMedium border border-solid border-red-primary;
      padding: calc($inputPadding / 2) $inputPadding;
      border-bottom-left-radius: var(--border-radius);
      border-bottom-right-radius: var(--border-radius);
      @apply text-red-primary;
      background-color: #f7e9eb;

      .icon {
        flex: none;
        margin-top: 2px;
      }

      .text {
        display: inline-block;
        position: relative;
        top: -1px;
        font-weight: bold;
        font-size: 15px;
        margin-left: 5px;
        word-break: break-word;
      }
    }

    &.check {
      input {
        padding-right: 36px;
      }
      .check {
        right: 14px;
        @apply text-green-primary;
      }
    }

    &.editIsVisible {
      input {
        padding-right: 68px;
      }

      .check.checkWithEdit {
        right: 50px;
      }

      .edit {
        right: 14px;
      }
    }

    &.label-position-animated,
    &.label-position-border {
      .label {
        color: #a4a4a4;
        position: absolute;
        left: 15px;
        top: 0;
        transition: 0.2s ease all;
        z-index: 10;
        padding: 0;
      }

      label {
        cursor: text;
      }

      input:focus ~ .label,
      input:valid ~ .label,
      input:disabled ~ .label,
      input[readonly] ~ .label {
        left: 26px;
        padding: 0 4px;
        top: -10px;
        font-size: 0.875rem;
        height: 20px;
        background: rgb(255, 255, 255);
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0) 40%,
          rgba(255, 255, 255, 1) 45%,
          rgba(255, 255, 255, 1) 100%
        );
      }

      // Need to do autofill separately because Firefox can't handle it
      input:-webkit-autofill ~ .label {
        left: 26px;
        padding: 0 4px;
        background-color: #fff;
        top: -10px;
        font-size: 0.875rem;
        height: 20px;
      }

      &.hideError {
        .label {
          @apply text-red-primary;
        }
      }
    }
  }
}

.placeholderIcon {
  @apply absolute h-full top-0 flex items-center pointer-events-none;
}

@screen md {
  .label:global(.label-position-border) {
    background: #fff;
  }
}
</style>
